/**
 * La scheda di un MARCHIO del registro di piattaforma (`marchi`, capitolo 162).
 *
 * Un marchio vero = una voce. Gli alias sono tutte le scritture con cui è stato trovato
 * («Sun Ballast», «SUNBALLAST»): servono a riconoscere un listino o un'etichetta scritti in
 * modo diverso. Rinominare il nome canonico rinomina `costruttore` su tutti i componenti
 * collegati, in un colpo solo: è una cache del nome, non una seconda verità.
 */
import React, { useEffect, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Divider,
  Drawer,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import { SoftChip } from '../SoftChip';
import { normalizzaNomeMarchio } from '@shared/anagrafe/identificativi';
import type { Marchio } from '../../types/anagrafe';
import { salvaMarchio } from '../../services/anagrafeService';

const ALTEZZA_TOCCO = 40;

interface Props {
  marchio: Marchio | null;
  /** Quanti componenti attivi puntano a questo marchio (per dire cosa cambia rinominando). */
  componentiCollegati: number;
  onChiudi: () => void;
  onAggiornato: (m: Marchio, componentiRinominati: number) => void;
  onEsito: (severity: 'success' | 'error', message: string) => void;
}

export const SchedaMarchio: React.FC<Props> = ({ marchio, componentiCollegati, onChiudi, onAggiornato, onEsito }) => {
  const [nome, setNome] = useState('');
  const [alias, setAlias] = useState<string[]>([]);
  const [nuovoAlias, setNuovoAlias] = useState('');
  const [formato, setFormato] = useState('');
  const [attivo, setAttivo] = useState(true);
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    if (!marchio) return;
    setNome(marchio.nome_canonico);
    setAlias(marchio.alias);
    setFormato(marchio.formato_matricola ?? '');
    setAttivo(marchio.attivo);
    setNuovoAlias('');
  }, [marchio]);

  if (!marchio) return null;

  const aggiungiAlias = () => {
    const a = normalizzaNomeMarchio(nuovoAlias);
    if (!a) return;
    if (!alias.includes(a)) setAlias([...alias, a].sort());
    setNuovoAlias('');
  };

  const nomeCambiato = nome.trim() !== '' && nome.trim() !== marchio.nome_canonico;

  const salva = async () => {
    if (nome.trim() === '') { onEsito('error', 'Il nome del marchio non può essere vuoto'); return; }
    setSalvando(true);
    try {
      const { marchio: aggiornato, componenti_rinominati } = await salvaMarchio(marchio.id, {
        ...(nomeCambiato ? { nome_canonico: nome.trim() } : {}),
        alias,
        formato_matricola: formato.trim() || undefined,
        attivo,
      });
      onAggiornato(aggiornato, componenti_rinominati);
      onEsito('success', componenti_rinominati > 0 ? `Marchio salvato, ${componenti_rinominati} componenti rinominati` : 'Marchio salvato');
    } catch (e) {
      console.error('[anagrafe] salvataggio marchio fallito:', e);
      onEsito('error', e instanceof Error ? e.message : 'Salvataggio fallito');
    } finally {
      setSalvando(false);
    }
  };

  return (
    <Drawer anchor="right" open onClose={onChiudi} PaperProps={{ sx: { width: { xs: '100%', sm: 520 } } }}>
      <Box sx={{ p: 3, pb: 8 }}>
        <Stack direction="row" alignItems="flex-start" spacing={2}>
          <Avatar src={marchio.logo_url ?? undefined} variant="rounded" sx={{ width: 56, height: 56, bgcolor: 'grey.100', color: 'grey.500', fontSize: 22 }}>
            {marchio.nome_canonico.slice(0, 1)}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6" sx={{ lineHeight: 1.2 }}>{marchio.nome_canonico}</Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 0.75, flexWrap: 'wrap', gap: 0.5 }}>
              <SoftChip label={`${componentiCollegati} componenti`} tone="success" />
              <SoftChip label={marchio.origine === 'manuale' ? 'curato a mano' : 'nato dalla corsa'} tone="default" />
              {!marchio.attivo && <SoftChip label="spento" tone="warning" />}
            </Stack>
          </Box>
          <IconButton onClick={onChiudi} aria-label="Chiudi la scheda" sx={{ width: ALTEZZA_TOCCO, height: ALTEZZA_TOCCO }}><CloseIcon /></IconButton>
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, fontFamily: 'var(--f-mono)' }}>{marchio.id}</Typography>

        <Divider sx={{ my: 2.5 }} />

        <Stack spacing={2}>
          <TextField
            label="nome del marchio"
            size="small"
            fullWidth
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            helperText={nomeCambiato ? `Rinominerà «${marchio.nome_canonico}» su ${componentiCollegati} componenti.` : 'Come compare nelle schede e nei documenti.'}
          />

          <Box>
            <Typography variant="body2" sx={{ mb: 0.75 }}>Altre scritture riconosciute</Typography>
            <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', gap: 0.75, mb: alias.length ? 1 : 0 }}>
              {alias.map((a) => (
                <Stack key={a} direction="row" alignItems="center" spacing={0.25}>
                  <SoftChip label={a} tone="default" mono />
                  <IconButton size="small" aria-label={`Togli l’alias ${a}`} onClick={() => setAlias(alias.filter((x) => x !== a))} sx={{ width: 32, height: 32 }}>
                    <DeleteOutline sx={{ fontSize: 16 }} />
                  </IconButton>
                </Stack>
              ))}
            </Stack>
            <Stack direction="row" spacing={1}>
              <TextField
                size="small"
                placeholder="aggiungi una scrittura (es. Huawei Technologies)"
                value={nuovoAlias}
                onChange={(e) => setNuovoAlias(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); aggiungiAlias(); } }}
                helperText="Si salva in maiuscolo, senza spazi né accenti: così un listino scritto diversamente si riconosce lo stesso."
                sx={{ flex: 1 }}
              />
              <Button variant="outlined" onClick={aggiungiAlias} sx={{ minHeight: ALTEZZA_TOCCO, alignSelf: 'flex-start' }}>Aggiungi</Button>
            </Stack>
          </Box>

          <TextField
            label="forma della matricola"
            size="small"
            fullWidth
            value={formato}
            onChange={(e) => setFormato(e.target.value)}
            placeholder="es. ^[A-Z0-9]{24}$"
            helperText="espressione regolare valida per tutti i modelli del marchio; il singolo modello può fare eccezione"
            inputProps={{ style: { fontFamily: 'var(--f-mono)', fontSize: 13 } }}
          />

          <FormControlLabel
            control={<Switch checked={attivo} onChange={(e) => setAttivo(e.target.checked)} />}
            label={attivo ? 'Marchio attivo' : 'Marchio spento: non proposto nelle schede'}
            sx={{ minHeight: ALTEZZA_TOCCO }}
          />

          {!attivo && componentiCollegati > 0 && (
            <Alert severity="warning" sx={{ py: 0.5 }}>
              {componentiCollegati} componenti puntano a questo marchio: spegnerlo li lascia collegati a un marchio che le schede non propongono più.
            </Alert>
          )}

          <Button variant="contained" onClick={salva} disabled={salvando} sx={{ alignSelf: 'flex-start', minHeight: ALTEZZA_TOCCO }}>
            {salvando ? <CircularProgress size={20} sx={{ color: 'inherit' }} /> : 'Salva marchio'}
          </Button>
        </Stack>
      </Box>
    </Drawer>
  );
};

export default SchedaMarchio;
